import { useId, useMemo } from 'react';
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import type { SelectOption, SelectTextFieldProps } from './SelectTextField.types';

function normalizeOptions(options: readonly string[] | readonly SelectOption[]): SelectOption[] {
  if (options.length === 0) return [];

  if (typeof options[0] === 'string') {
    return (options as readonly string[]).map((opt) => ({ label: opt, value: opt }));
  }

  return [...(options as readonly SelectOption[])];
}

/**
 * Searchable single-select field. Accepts options as `string[]` or
 * `{label, value}[]` and reports the selected `value` as a plain string, so a
 * react-hook-form `Controller` can drive it without a mapper.
 */
export function SelectTextField(props: SelectTextFieldProps) {
  const { t } = useTranslation('common');

  const {
    options,
    value,
    onChange,
    label,
    placeholder,
    error = false,
    helperText,
    required,
    disabled = false,
    name,
    onBlur,
    freeSolo = false,
    id,
  } = props;

  const generatedId = useId();
  const inputId = id ?? generatedId;

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedOption = useMemo(
    () => normalizedOptions.find((opt) => opt.value === value) ?? null,
    [normalizedOptions, value],
  );

  return (
    <Field data-invalid={error ? true : undefined}>
      {label !== undefined && (
        <FieldLabel htmlFor={inputId}>
          {label}
          {required === true && (
            <span aria-hidden="true" className="text-destructive">
              *
            </span>
          )}
        </FieldLabel>
      )}
      <Combobox<SelectOption>
        items={normalizedOptions}
        value={selectedOption}
        onValueChange={(next) => onChange(next?.value ?? '')}
        disabled={disabled}
        itemToStringLabel={(option) => option.label}
        itemToStringValue={(option) => option.value}
        isItemEqualToValue={(a, b) => a.value === b.value}
        // In freeSolo mode the raw text IS the value, so the input text is
        // controlled straight off `value`; every keystroke commits. Without this
        // the field would silently discard anything not in `options`.
        {...(freeSolo ? { inputValue: value, onInputValueChange: onChange } : {})}
      >
        <ComboboxInput
          id={inputId}
          name={name}
          placeholder={placeholder}
          onBlur={onBlur}
          aria-invalid={error || undefined}
          aria-required={required || undefined}
        />
        <ComboboxContent className="pointer-events-auto">
          <ComboboxList>
            <ComboboxCollection>
              {(option: SelectOption) => (
                <ComboboxItem key={option.value} value={option}>
                  <span className="truncate">{option.label}</span>
                </ComboboxItem>
              )}
            </ComboboxCollection>
            <ComboboxEmpty>{t('ui.selectTextField.noResults')}</ComboboxEmpty>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {helperText !== undefined && helperText !== '' ? (
        error ? (
          <FieldError>{helperText}</FieldError>
        ) : (
          <FieldDescription>{helperText}</FieldDescription>
        )
      ) : null}
    </Field>
  );
}
